import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import API_BASE_URL from '../config/api';

function OrderConfirmation() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrderDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}`);
      if (!response.ok) throw new Error('Order not found');
      const data = await response.json();
      setOrder(data.data.order);
      setItems(data.data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const handleContinueShopping = () => {
    navigate('/products');
  };

  const handleViewOrders = () => {
    navigate('/profile');
  };

  // Calculate GST
  const calculateGST = () => {
    let totalGST = 0;
    items.forEach(item => {
      const gstRate = item.gst || 5;
      totalGST += Math.round((item.price * item.quantity) * gstRate / (100 + gstRate));
    });
    return totalGST;
  };

  const gstAmount = calculateGST();
  const subtotal = items.reduce((sum, item) => sum + Math.round(item.price * item.quantity), 0);
  const shippingCost = Number(order?.shippingCost) || 0;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="text-center">
          <div style={{ fontSize: '4rem', marginBottom: spacing.lg }}>⏳</div>
          <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text.primary }}>Loading order details...</p>
        </motion.div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: spacing.xl }}>
        <motion.div style={{ textAlign: 'center', backgroundColor: 'white', borderRadius: '1rem', padding: spacing.xl, boxShadow: shadows.md }} initial={{ scale: 0.9 }} animate={{ scale: 1 }}>
          <div style={{ fontSize: '4rem', marginBottom: spacing.lg }}>❌</div>
          <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.md }}>{error || 'Order not found'}</p>
          <motion.button
            onClick={() => navigate('/products')}
            style={{ backgroundColor: colors.primary[600], color: 'white', padding: `${spacing.md} ${spacing.xl}`, border: 'none', borderRadius: '0.5rem', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }}
            whileHover={{ backgroundColor: colors.primary[700] }}
            whileTap={{ scale: 0.95 }}
          >
            Continue Shopping
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Status flags - ONLY based on status field, NOT on paymentMethod
  const isCOD_Pending = order.status === 'cod_pending';          // COD: waiting for admin approval
  const isPending = order.status === 'payment_pending';          // Online UPI: waiting for payment
  const isCustomerPaid = order.status === 'customer_paid';        // Online UPI: user marked as paid, waiting for admin verification
  const isConfirmed = order.status === 'confirmed' || order.status === 'processing' || order.status === 'shipped' || order.status === 'delivered';  // Admin confirmed
  const isFailed = order.status === 'payment_failed';             // Payment failed

  const shippingMethodDisplay = order.shippingMethod || '';
  const estimatedDate = order.estimatedDelivery ? new Date(order.estimatedDelivery) : null;
  const formattedDate = estimatedDate ? estimatedDate.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }) : '3–5 business days';

  // Determine header appearance
  const isAwaitingAction = isCOD_Pending || isCustomerPaid;
  const orderTotal = Math.round(order.totalAmount || 0);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      {/* Header */}
      <motion.div
        style={{
          background: isConfirmed
            ? `linear-gradient(135deg, ${colors.success} 0%, #16a34a 100%)`
            : isFailed
            ? `linear-gradient(135deg, ${colors.error} 0%, #dc2626 100%)`
            : `linear-gradient(135deg, ${colors.accent[500]} 0%, #0d9488 100%)`,
          color: 'white',
          padding: `${spacing.xl} ${spacing.lg}`,
          textAlign: 'center',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div style={{ maxWidth: '48rem', margin: '0 auto' }}>
          <motion.div
            style={{ fontSize: '4rem', marginBottom: spacing.md }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
          >
            {isConfirmed ? '🎉' : isFailed ? '❌' : '📦'}
          </motion.div>
          <motion.h1
            style={{ fontSize: '2rem', fontWeight: 900, margin: 0 }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {isConfirmed
              ? '✅ Payment Received'
              : isFailed
              ? 'Payment Failed'
              : '📦 Order Received'}
          </motion.h1>
          <motion.p
            style={{ margin: `${spacing.sm} 0 0`, opacity: 0.9, fontSize: '1rem' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Order #{order.id} — {isConfirmed
              ? `Your order has been confirmed and payment received. It will arrive by ${formattedDate}.`
              : isFailed
              ? 'Your payment could not be verified. Please try again.'
              : isCOD_Pending
              ? `Your order has been placed successfully! Please wait for our team to confirm your order. You will receive a confirmation call. Pay ₹${orderTotal} in cash at delivery.`
              : 'Thank you! Your payment is being verified by our team.'}
          </motion.p>
        </div>
      </motion.div>

      <div style={{ maxWidth: '48rem', margin: '0 auto', padding: spacing.xl }}>
        {/* Status Card */}
        <motion.div
          style={{
            backgroundColor: 'white',
            borderRadius: '1rem',
            padding: spacing.xl,
            boxShadow: shadows.md,
            marginBottom: spacing.xl,
            borderLeft: `6px solid ${isConfirmed ? colors.success : isFailed ? colors.error : '#0d9488'}`,
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.md }}>
            <div>
              <p style={{ margin: 0, fontSize: '0.85rem', color: colors.text.secondary, fontWeight: 600 }}>ORDER STATUS</p>
              <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '1.5rem', fontWeight: 800, color: isConfirmed ? colors.success : isFailed ? colors.error : '#0d9488' }}>
                {isConfirmed
                  ? '✅ Confirmed - Payment Received'
                  : isFailed
                  ? '❌ Failed'
                  : isCOD_Pending
                  ? '📦 Awaiting Confirmation'
                  : isCustomerPaid
                  ? '⏳ Verifying Payment'
                  : '⏳ Pending'}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: colors.text.secondary, fontWeight: 600 }}>TOTAL</p>
              <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '1.5rem', fontWeight: 900, color: colors.primary[600] }}>₹{orderTotal}</p>
            </div>
          </div>

          {isCOD_Pending && (
            <motion.div
              style={{ backgroundColor: '#f0fdf4', borderRadius: '0.75rem', padding: spacing.lg, marginTop: spacing.md, border: '1px solid #99f6e4' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                <div style={{ fontSize: '2rem' }}>📦</div>
                <div>
                  <p style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f766e' }}>Order Received - Wait for Confirmation</p>
                  <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.9rem', color: '#14b8a6' }}>Your order has been placed successfully. Our team will review and confirm your order within 24 hours. You will receive a confirmation call on your registered phone number. Please keep ₹{orderTotal} ready for cash payment at delivery.</p>
                </div>
              </div>
            </motion.div>
          )}

          {isConfirmed && (
            <motion.div
              style={{ backgroundColor: '#f0fdf4', borderRadius: '0.75rem', padding: spacing.lg, marginTop: spacing.md }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                <div style={{ fontSize: '2rem' }}>🚚</div>
                <div>
                  <p style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#166534' }}>Estimated Delivery</p>
                  <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '1.25rem', fontWeight: 800, color: '#15803d' }}>{formattedDate}</p>
                  <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.85rem', color: '#4ade80' }}>Your order will arrive by this date.</p>
                </div>
              </div>
            </motion.div>
          )}

          {isCustomerPaid && (
            <motion.div
              style={{ backgroundColor: colors.accent[50], borderRadius: '0.75rem', padding: spacing.lg, marginTop: spacing.md }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                <div style={{ fontSize: '2rem' }}>⏳</div>
                <div>
                  <p style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: colors.accent[700] }}>Awaiting Verification</p>
                  <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.9rem', color: colors.accent[600] }}>Please wait while we verify your payment. This usually takes a few minutes.</p>
                </div>
              </div>
            </motion.div>
          )}

          {isFailed && (
            <motion.div
              style={{ backgroundColor: '#fef2f2', borderRadius: '0.75rem', padding: spacing.lg, marginTop: spacing.md }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                <div style={{ fontSize: '2rem' }}>⚠️</div>
                <div>
                  <p style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#991b1b' }}>Payment Not Verified</p>
                  <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.9rem', color: '#b91c1c' }}>We could not verify your payment. If you have already paid, please contact support.</p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Order Items with GST Breakdown */}
        <motion.div
          style={{ backgroundColor: 'white', borderRadius: '1rem', padding: spacing.lg, boxShadow: shadows.md, marginBottom: spacing.xl }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 style={{ margin: `0 0 ${spacing.lg}`, fontSize: '1.25rem', fontWeight: 800, color: colors.text.primary }}>📦 Order Items</h2>
          
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 80px 100px 120px',
            gap: spacing.md,
            padding: `${spacing.sm} ${spacing.sm}`,
            backgroundColor: colors.primary[25],
            borderRadius: '0.5rem',
            marginBottom: spacing.md,
            fontWeight: 700,
            fontSize: '0.85rem',
            color: colors.text.secondary,
          }}>
            <span>Item</span>
            <span style={{ textAlign: 'center' }}>Qty</span>
            <span style={{ textAlign: 'right' }}>Price</span>
            <span style={{ textAlign: 'right' }}>Total</span>
          </div>

          {/* Item Rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, marginBottom: spacing.lg }}>
            {items.map((item, idx) => {
              const itemTotal = Math.round(item.price * item.quantity);
              return (
                <motion.div
                  key={idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 80px 100px 120px',
                    gap: spacing.md,
                    padding: `${spacing.md} ${spacing.sm}`,
                    borderBottom: idx < items.length - 1 ? `1px solid ${colors.border}` : 'none',
                    alignItems: 'center',
                  }}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + idx * 0.05 }}
                >
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, color: colors.text.primary, fontSize: '0.95rem' }}>{item.name}</p>
                    {(item.size_name || item.side) && (
                      <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.8rem', color: colors.text.secondary }}>
                        {item.size_name ? `Size: ${item.size_name}` : ''}
                        {item.size_name && item.side ? ' — ' : ''}
                        {item.side ? `${item.side === 'right' ? 'Right' : 'Left'} side` : ''}
                      </p>
                    )}
                  </div>
                  <p style={{ margin: 0, fontWeight: 600, color: colors.text.secondary, textAlign: 'center' }}>{item.quantity}</p>
                  <p style={{ margin: 0, fontWeight: 600, color: colors.text.secondary, textAlign: 'right' }}>₹{Math.round(item.price)}</p>
                  <p style={{ margin: 0, fontWeight: 700, color: colors.text.primary, textAlign: 'right' }}>₹{itemTotal}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Price Breakdown */}
          <div style={{
            borderTop: `2px solid ${colors.border}`,
            paddingTop: spacing.lg,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.sm,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.95rem', color: colors.text.secondary }}>Subtotal (Items)</span>
              <span style={{ fontSize: '0.95rem', fontWeight: 600, color: colors.text.primary }}>₹{Math.round(subtotal)}</span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f0fdf4',
              padding: `${spacing.sm} ${spacing.md}`,
              borderRadius: '0.4rem',
            }}>
              <span style={{ fontSize: '0.95rem', color: '#166534', fontWeight: 600 }}>
                🧾 GST (5% included)
              </span>
              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#16a34a' }}>₹{gstAmount}</span>
            </div>

            {shippingMethodDisplay && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: colors.primary[25],
                padding: `${spacing.sm} ${spacing.md}`,
                borderRadius: '0.4rem',
              }}>
                <span style={{ fontSize: '0.95rem', color: colors.primary[700], fontWeight: 600 }}>
                  🚚 Shipping ({shippingMethodDisplay === 'Rapido Parcel' ? 'Rapido' : shippingMethodDisplay === 'Normal Porter' ? 'Porter' : shippingMethodDisplay})
                </span>
                <span style={{ fontSize: '0.95rem', fontWeight: 700, color: colors.primary[600] }}>
                  {shippingCost > 0 ? `₹${shippingCost}` : 'Free'}
                </span>
              </div>
            )}

            {shippingMethodDisplay && estimatedDate && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', color: colors.text.secondary }}>
                <span>📅 Estimated Delivery</span>
                <span>{formattedDate}</span>
              </div>
            )}

            <div style={{ borderTop: `2px solid ${colors.border}`, margin: `${spacing.sm} 0` }}></div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: '1.2rem', fontWeight: 800, color: colors.text.primary }}>Total</span>
              <span style={{ fontSize: '1.75rem', fontWeight: 900, color: colors.primary[600] }}>₹{orderTotal}</span>
            </div>

            {isCOD_Pending && (
              <div style={{ padding: `${spacing.md} ${spacing.lg}`, backgroundColor: '#f0fdf4', borderRadius: '0.5rem', border: '1px solid #99f6e4', marginTop: spacing.sm }}>
                <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: '#0f766e', display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                  <span>💵</span> Pay <strong>₹{orderTotal}</strong> in cash at the time of delivery
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div style={{ display: 'flex', gap: spacing.md }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <motion.button
            onClick={handleContinueShopping}
            style={{ flex: 1, padding: spacing.lg, backgroundColor: colors.primary[600], color: 'white', border: 'none', borderRadius: '0.75rem', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}
            whileHover={{ backgroundColor: colors.primary[700] }}
            whileTap={{ scale: 0.95 }}
          >
            🛍️ Continue Shopping
          </motion.button>
          <motion.button
            onClick={handleViewOrders}
            style={{ flex: 1, padding: spacing.lg, backgroundColor: colors.neutral[200], color: colors.text.primary, border: 'none', borderRadius: '0.75rem', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}
            whileHover={{ backgroundColor: colors.neutral[300] }}
            whileTap={{ scale: 0.95 }}
          >
            📋 View My Orders
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
}

export default OrderConfirmation;